export interface SellerScoreStats {
  accountAgeMonths: number;
  totalSales: number;
  averageRating: number | null;
  reviewCount: number;
  assetCount: number;
  followerCount: number;
  refundCount: number;
  totalTransactions: number;
  isEarlyFounder?: boolean;
  commissionCount?: number;
  commissionAvgRating?: number | null;
}

export interface ScoreBreakdown {
  accountAge: number;
  sales: number;
  rating: number;
  reviews: number;
  assets: number;
  followers: number;
  refundPenalty: number;
  founderBonus: number;
  commissions: number;
}

export type SellerTier = "new" | "rising" | "established" | "top" | "elite";

export interface SellerScoreResult {
  score: number;
  tier: SellerTier;
  breakdown: ScoreBreakdown;
}

export const TIER_CONFIG: Record<SellerTier, { label: string; color: string; bgClass: string }> = {
  new: { label: "New Seller", color: "hsl(var(--muted-foreground))", bgClass: "bg-muted text-muted-foreground" },
  rising: { label: "Rising Seller", color: "hsl(210 100% 50%)", bgClass: "bg-blue-500/15 text-blue-500" },
  established: { label: "Established", color: "hsl(142 76% 36%)", bgClass: "bg-emerald-500/15 text-emerald-500" },
  top: { label: "Top Seller", color: "hsl(271 91% 65%)", bgClass: "bg-violet-500/15 text-violet-500" },
  elite: { label: "Elite Seller", color: "hsl(45 93% 47%)", bgClass: "bg-amber-500/15 text-amber-600" },
};

function getTier(score: number): SellerTier {
  if (score >= 90) return "elite";
  if (score >= 70) return "top";
  if (score >= 50) return "established";
  if (score >= 30) return "rising";
  return "new";
}

/**
 * The most each component can contribute. The breakdown tooltip reads these, so
 * the "x/max" it shows is the cap the score actually applies — it had drifted to
 * Sales /25 and Rating /30 after those caps were lowered to make room for
 * commissions.
 */
export const SCORE_COMPONENT_MAX = {
  accountAge: 15,
  sales: 20,
  rating: 25,
  reviews: 10,
  assets: 10,
  followers: 10,
  commissions: 10,
  founderBonus: 10,
} as const;

export function calculateSellerScore(stats: SellerScoreStats): SellerScoreResult {
  const MAX = SCORE_COMPONENT_MAX;
  // Account Age: up to 15 points
  const accountAge = Math.min(stats.accountAgeMonths, MAX.accountAge);

  // Downloads & sales volume: up to 20 points (reduced from 25 to make room for commissions)
  const sales = Math.min(Math.floor(stats.totalSales / 2), MAX.sales);

  // Average Rating: up to 25 points (blended with commission ratings)
  let blendedRating = stats.averageRating;
  if (stats.commissionAvgRating !== undefined && stats.commissionAvgRating !== null) {
    if (blendedRating !== null) {
      blendedRating = (blendedRating + stats.commissionAvgRating) / 2;
    } else {
      blendedRating = stats.commissionAvgRating;
    }
  }
  const rating = blendedRating !== null
    ? Math.round((blendedRating / 5) * MAX.rating)
    : 0;

  // Review Count: up to 10 points
  const reviews = Math.min(Math.floor(stats.reviewCount / 5), MAX.reviews);

  // Asset Count: up to 10 points
  const assets = Math.min(Math.floor(stats.assetCount / 2), MAX.assets);

  // Follower Count: up to 10 points
  const followers = Math.min(Math.floor(stats.followerCount / 10), MAX.followers);

  // Commission Count: up to 10 points (1 per 3 completed commissions)
  const commissions = Math.min(Math.floor((stats.commissionCount || 0) / 3), MAX.commissions);

  // Refund penalty
  let refundPenalty = 0;
  if (stats.totalTransactions > 0) {
    const refundRate = stats.refundCount / stats.totalTransactions;
    if (refundRate > 0.1) refundPenalty = -15;
    else if (refundRate > 0.05) refundPenalty = -5;
  }

  // Early Founder bonus: +10 points
  const founderBonus = stats.isEarlyFounder ? MAX.founderBonus : 0;

  const rawScore = accountAge + sales + rating + reviews + assets + followers + commissions + refundPenalty + founderBonus;
  const score = Math.max(0, Math.min(100, rawScore));

  return {
    score,
    tier: getTier(score),
    breakdown: {
      accountAge,
      sales,
      rating,
      reviews,
      assets,
      followers,
      refundPenalty,
      founderBonus,
      commissions,
    },
  };
}
